import type { AdapterIoResultListApiData } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import {
  ApiErrorPageInternal,
  enrichFiltersWithCounts,
  type FilterUnit,
  filterUnitFor,
  mapFilterControls,
  NavioPage,
  NoddiTable,
  useFilterUnits,
  useNoddiQueryParam,
  useNoddiSortOrderParams,
  usePaginationQueryParam
} from 'noddi-ui-web';
import {
  ADAPTER_IO_ENTITY_TYPE_OPTIONS,
  type AdapterIOEntityTypeValue,
  addDays,
  businessTime,
  NoddiDateFormats,
  noddiFormatDate
} from 'noddi-util';
import { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { StringParam } from 'use-query-params';
import { useAdapterIOTableFilters } from '../../hooks/filters/Other/useAdapterIOTableFilters';
import { adapterLinkedFilterUnit } from '../../hooks/filters/Other/useAdapterLinkedDomainFilters';
import { AdapterIOEntityTabs } from './AdapterIOEntityTabs';
import { withCountedOptions } from './adapterIoListCells';
import { getAdapterIOResultsColumns } from './adapterIoResultsColumns';
import {
  type AdapterIOEntityTab,
  getAdapterIoResultsRowActions,
  getAdapterIOViewCopy,
  getAdapterIOViewPolicy,
  resolveAdapterIOViewTab
} from './adapterIoResultsPageHelpers';
import { AdapterReviewCasesPage } from './AdapterReviewCasesPage';
import type { AdapterRoutes } from './types';

type AdapterIoResultQuery = Omit<
  NonNullable<AdapterIoResultListApiData['query']>,
  'pageIndex' | 'pageSize' | 'ordering' | 'search'
>;

const adapterIoResultFilterUnit = filterUnitFor<AdapterIoResultQuery>();

type Props = {
  routes: AdapterRoutes;
  /**
   * Scope narrowing supplied by the host app; it also scopes filter options.
   * - omit / `undefined`: unscoped fetch (admin default)
   * - non-empty array: filter to those departments
   * - empty array: redirect home (partner with no sidebar selection)
   */
  serviceDepartmentIds?: number[];
  /**
   * Host filters rendered before the shared adapter I/O filters, in the I/O results view only. When
   * given, they own `serviceDepartmentIds` there; the Needs review view still scopes by the prop.
   */
  hostFilters?: FilterUnit<Pick<AdapterIoResultQuery, 'serviceDepartmentIds' | 'excludedServiceDepartmentIds'>>;
};

/**
 * Unified adapter I/O list (push + pull on entity-centric subjects).
 * One preferred step row per subject; open detail for full chain timeline.
 */
export function AdapterIOResultsPage({ routes, serviceDepartmentIds, hostFilters }: Props) {
  const navigate = useNavigate();
  const [viewTab, setViewTab] = useNoddiQueryParam('io-view', StringParam);
  const selectedView = resolveAdapterIOViewTab(viewTab);
  const [entityTab, setEntityTab] = useNoddiQueryParam('io-entity', StringParam);
  const selectedEntity: AdapterIOEntityTab =
    entityTab === 'all'
      ? 'all'
      : ADAPTER_IO_ENTITY_TYPE_OPTIONS.some((option) => option.value === entityTab)
        ? (entityTab as AdapterIOEntityTypeValue)
        : 'all';
  // Empty arrays are skipped by django-filter — never fetch "unscoped" when the host meant "none".
  const canFetch = serviceDepartmentIds === undefined || serviceDepartmentIds.length > 0;
  const { pagination, setPagination } = usePaginationQueryParam();
  const todayBookingDate = useMemo(() => noddiFormatDate(businessTime(new Date()), NoddiDateFormats.API_DATE), []);
  const tomorrowBookingDate = useMemo(
    () => noddiFormatDate(addDays(businessTime(new Date()), 1), NoddiDateFormats.API_DATE),
    []
  );
  const viewPolicy = getAdapterIOViewPolicy({
    selectedView,
    selectedEntity,
    todayBookingDate,
    tomorrowBookingDate
  });
  const { showBookingDateTime } = viewPolicy;
  const { resultFilters, reviewFilters } = useAdapterIOTableFilters({
    entityType: selectedEntity !== 'all' ? selectedEntity : undefined,
    serviceDepartmentIds,
    baseExcludedStatusIds: viewPolicy.kind === 'io-results' ? viewPolicy.baseExcludedStatusIds : undefined
  });
  // Unscoped (`undefined`) sends no department; `[]` never fetches (see `canFetch`).
  const departmentScope = adapterLinkedFilterUnit.locked({ serviceDepartmentIds });
  const ioResultFilters = useFilterUnits([
    hostFilters ?? departmentScope,
    resultFilters,
    adapterIoResultFilterUnit.locked(viewPolicy.kind === 'io-results' ? viewPolicy.apiInput : {})
  ]);
  // Host filters render in the I/O results view only, yet the host's departments still scope this queue.
  const reviewCaseFilters = useFilterUnits([departmentScope, reviewFilters]);
  const columns = getAdapterIOResultsColumns({ showBookingDateTime });
  const { order, onOrderChange } = useNoddiSortOrderParams({
    columns,
    defaultOrdering: viewPolicy.kind === 'io-results' ? viewPolicy.defaultOrdering : undefined
  });
  const canFetchFilterCounts = canFetch && pagination.search.length === 0;

  const { data, isPending, isFetching, error } = noddiAsync.useGet({
    type: URLKeys.getAdapterIoResults,
    input: {
      ...pagination,
      ...ioResultFilters.apiInput,
      order
    },
    queryConfig: {
      enabled: canFetch && viewPolicy.kind === 'io-results',
      placeholderData: keepPreviousData
    }
  });

  const {
    data: resultFilterCounts,
    error: resultFilterCountsError,
    refetch: refetchResultFilterCounts
  } = noddiAsync.useGet({
    type: URLKeys.getAdapterIOFilterCounts,
    input: ioResultFilters.apiInput,
    queryConfig: {
      enabled: canFetchFilterCounts && viewPolicy.kind === 'io-results',
      placeholderData: keepPreviousData
    }
  });

  const {
    data: reviewFilterCounts,
    error: reviewFilterCountsError,
    refetch: refetchReviewFilterCounts
  } = noddiAsync.useGet({
    type: URLKeys.getAdapterReviewCaseFilterCounts,
    input: reviewCaseFilters.apiInput,
    queryConfig: {
      enabled: canFetchFilterCounts && viewPolicy.kind === 'review-cases',
      placeholderData: keepPreviousData
    }
  });

  const visibleResultFilterCounts = canFetchFilterCounts ? resultFilterCounts : undefined;

  // Keyed by control id, which for builders is the param.
  const resultFilterCountMap = {
    directions: visibleResultFilterCounts?.directions,
    statuses: visibleResultFilterCounts?.statuses,
    failureReasons: visibleResultFilterCounts?.failureReasons,
    adapterTypes: visibleResultFilterCounts?.adapterTypes,
    unresolved: visibleResultFilterCounts?.unresolved
  };
  const countedIoResultFilters = mapFilterControls(ioResultFilters, (action) => {
    // Options first: a reason the backend reports but the static list omits is
    // otherwise visible in the rows and impossible to filter by.
    const withOptions = withCountedOptions([action], resultFilterCountMap)[0] ?? action;
    return enrichFiltersWithCounts([withOptions], resultFilterCountMap)[0] ?? withOptions;
  });

  const activeFilterCounts = canFetchFilterCounts
    ? viewPolicy.kind === 'review-cases'
      ? reviewFilterCounts
      : resultFilterCounts
    : undefined;
  const activeFilterCountsError =
    canFetchFilterCounts && (viewPolicy.kind === 'review-cases' ? reviewFilterCountsError : resultFilterCountsError);
  const refetchActiveFilterCounts =
    viewPolicy.kind === 'review-cases' ? refetchReviewFilterCounts : refetchResultFilterCounts;
  const entityCountRows = activeFilterCounts?.entityTypes;
  const entityTotalCount = activeFilterCounts?.total;

  const rowActions = useMemo(
    () =>
      getAdapterIoResultsRowActions({
        routes,
        navigate: (url) => {
          void navigate(url);
        }
      }),
    [navigate, routes]
  );

  if (serviceDepartmentIds !== undefined && serviceDepartmentIds.length === 0) {
    return <Navigate to={routes.home.getPath()} replace />;
  }

  if (error) {
    return <ApiErrorPageInternal error={error} />;
  }

  const { title, description } = getAdapterIOViewCopy(selectedView, tomorrowBookingDate);

  const listing = (
    <div className='flex min-h-0 flex-1 flex-col gap-5'>
      <AdapterIOEntityTabs
        entityCounts={entityCountRows}
        totalCount={entityTotalCount}
        value={selectedEntity}
        onValueChange={(value) => {
          setEntityTab(value);
          setPagination({ ...pagination, pageIndex: 0 });
        }}
        hasError={Boolean(activeFilterCountsError)}
        onRetry={() => {
          void refetchActiveFilterCounts();
        }}
      />
      {viewPolicy.kind === 'review-cases' ? (
        <AdapterReviewCasesPage
          routes={routes}
          filters={reviewCaseFilters}
          defaultOrdering={viewPolicy.defaultOrdering}
          showBookingDateTime={showBookingDateTime}
        />
      ) : (
        <NoddiTable
          fillHeight
          data={data?.results ?? []}
          columns={columns}
          pagination={pagination}
          setPagination={setPagination}
          totalCount={data?.count}
          isPending={isPending}
          isFetching={isFetching}
          ordering={order}
          onOrderChange={onOrderChange}
          filters={countedIoResultFilters}
          actions={rowActions}
          searchPlaceholder='Search plate, customer, external id, booking or order...'
          getRowId={(row) => row.id}
          getUrlPath={(row) => ({
            url: routes.adapterPushResultDetail.getPath({
              ioResultId: row.id
            })
          })}
        />
      )}
    </div>
  );

  return (
    <NavioPage
      fillHeight
      title={title}
      description={description}
      breadcrumbs={[{ title: 'Home', path: routes.home.getPath() }, { title: 'Adapter I/O' }]}
      variant='secondary'
      // The views filter one list; only the selected panel renders it.
      tabs={[
        { label: 'All I/O', value: 'all', node: listing },
        { label: 'Tomorrow', value: 'tomorrow', node: listing },
        { label: 'Needs review', value: 'needs-review', node: listing }
      ]}
      defaultTab='all'
      searchParamName='io-view'
      keepExistingSearchParams
      tabValue={selectedView}
      onTabChange={(value) => {
        setViewTab(value);
        setPagination({ ...pagination, pageIndex: 0 });
      }}
    />
  );
}
